import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode, useRef} from 'react';

import {updateJellyfish} from 'app-core/archetype/JellyfishDAO';
import {JellyfishViewQuery} from 'app-core/archetype/workspace/__generated/JellyfishViewQuery';
import {asCoreObject} from 'app-core/framework/CoreObjectIntrospectionUtils';
import CoreObjectStarIconRenderer from 'app-core/platform/starrable/CoreObjectStarIcon';
import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import AliasField from 'metadata/AliasField';
import getTestProps from 'test/lib/cypress/getTestProps';

import {StarGraphQL} from '../../../../graphql-fragments/app-core/platform/starrable/__generated/StarGraphQL';

type Jellyfish = ExtractByTypename<JellyfishViewQuery['jellyfish'], 'JellyfishNode'>;

type Tentacle = Jellyfish['tentacles'][number];

async function updateJellyfishName(id: string, name: string): Promise<void> {
  await updateJellyfish({id, name});
}

interface JellyfishNameEditorProps {
  name: string;
  handleUpdateName: (newName: string) => void;
}

function JellyfishNameEditor({name, handleUpdateName}: JellyfishNameEditorProps): JSX.Element {
  const aliasRef = useRef<AliasField>(null);

  return (
    <div
      className="metadata-header-section"
      {...getTestProps({component: 'JellyfishNameEditor', element: 'root'})}
    >
      <AliasField
        ref={aliasRef}
        initialAlias={name}
        isFullWidth={false}
        isHeader={true}
        isWritable={true}
        complete={handleUpdateName}
        placeholder="Name"
      />
    </div>
  );
}

interface DetailsRowProps {
  title: string;
  children: ReactNode;
}

function DetailsRow({title, children}: DetailsRowProps): JSX.Element {
  return (
    <div className="metadata-section">
      <div className="metadata-header">{title}</div>
      {children}
    </div>
  );
}

interface TentacleRowProps {
  isParentArchived: boolean;
  tentacle: Tentacle;
  reload: () => void;
}

function TentacleRow({isParentArchived, tentacle, reload}: TentacleRowProps): JSX.Element {
  const {name, archiveData} = tentacle;
  const {archived} = archiveData;
  return (
    <li className={css(styles.marginBottom)}>
      <div className={css(styles.flexCenter)}>
        <div className={css(styles.archiveStatus)}>
          {`${name} | ${archived ? 'Archived' : 'Not archived'}`}
        </div>
        <ArchiveOrUnarchiveButton
          item={tentacle}
          isArchivable={Boolean(tentacle.permissions.archivable)}
          isUnarchivable={!isParentArchived && Boolean(tentacle.permissions.unarchivable)}
          onArchive={reload}
          onUnarchive={reload}
        />
      </div>
    </li>
  );
}

interface JellyfishDetailsProps {
  jellyfish: Jellyfish;
  star?: StarGraphQL | null;
  reload: () => void;
}

export default function JellyfishDetails({jellyfish, reload}: JellyfishDetailsProps): JSX.Element {
  const {id, name, tentacles, archiveData, star} = jellyfish;
  const {archived} = archiveData;
  const isStarred = star?.isStarred || false;

  async function handleUpdateName(newName: string): Promise<void> {
    await updateJellyfishName(id, newName);
    reload();
  }

  return (
    <div className="react-tab-container" {...getTestProps({component: 'JellyfishDetails', element: 'root'})}>
      <JellyfishNameEditor name={name} handleUpdateName={handleUpdateName} />
      <DetailsRow title="Starred">
        {isStarred ? 'Starred ' : 'Not starred '}
        <CoreObjectStarIconRenderer item={asCoreObject(jellyfish)} isStarred={isStarred} />
      </DetailsRow>
      <DetailsRow title="Archived">
        <div
          className={css(styles.flexCenter)}
          {...getTestProps({component: 'JellyfishDetails', element: 'archiveDetailsRow'})}
        >
          <div
            className={css(styles.archiveStatus)}
            {...getTestProps({component: 'JellyfishDetails', element: 'archiveStatus'})}
          >
            {archived ? 'Archived' : 'Not archived'}
            <ArchiveRecordLabel item={jellyfish} />
          </div>
          <ArchiveOrUnarchiveButton
            item={jellyfish}
            onArchive={ArchiveActions.bulkArchiveFilesWithDataMods}
            onUnarchive={ArchiveActions.bulkUnarchiveFilesWithDataMods}
            isArchivable={Boolean(jellyfish.permissions.archivable)}
            isUnarchivable={Boolean(jellyfish.permissions.unarchivable)}
          />
        </div>
      </DetailsRow>
      <DetailsRow title="Tentacles">
        <ul>
          {tentacles.map((tentacle) => (
            <TentacleRow key={tentacle.id} tentacle={tentacle} isParentArchived={archived} reload={reload} />
          ))}
        </ul>
      </DetailsRow>
    </div>
  );
}

const styles = StyleSheet.create({
  marginBottom: {
    marginBottom: 8,
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  archiveStatus: {
    marginRight: 8,
  },
});
